import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './CalendarPage.css';

function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'list' or 'calendar'

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async () => {
    try {
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setEvents(eventsData);
      setLoading(false);
    } catch (error) {
      console.error('일정 데이터 가져오기 오류:', error);
      setLoading(false);
    }
  };

  // 이벤트 타입별 아이콘
  const getEventIcon = (type) => {
    const icons = {
      'rental': '⚽',
      'no-rental': '🚫',
      'event': '📌',
      'tournament': '🏆',
      'family-event': '🎊',
      'holiday': '🎉'
    };
    return icons[type] || '📅';
  };

  // 이벤트 타입별 라벨
  const getEventTypeLabel = (type) => {
    const labels = {
      'rental': '의왕축구장',
      'no-rental': '대관없음',
      'event': '행사',
      'tournament': '대회',
      'family-event': '경조사',
      'holiday': '연휴'
    };
    return labels[type] || type;
  };

  // 다가오는 일정 가져오기 (오늘 포함 향후 5개)
  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allEvents = [];

    // 명시적 이벤트 추가
    events.forEach(event => {
      const eventDate = new Date(event.date + 'T00:00:00');
      if (eventDate >= today) {
        allEvents.push({
          ...event,
          dateObj: eventDate,
          isAutoRental: false
        });
      }
    });

    // 자동 일요일 대관 추가 (향후 8주)
    for (let i = 0; i < 56; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);

      if (checkDate.getDay() === 0) { // 일요일
        const dateStr = formatDate(checkDate);
        const hasNoRental = events.some(e => e.date === dateStr && e.type === 'no-rental');
        const hasExplicitRental = events.some(e => e.date === dateStr && e.type === 'rental');

        if (!hasNoRental && !hasExplicitRental) {
          allEvents.push({
            id: 'auto-' + dateStr,
            type: 'rental',
            title: '정기 대관',
            date: dateStr,
            dateObj: checkDate,
            location: '의왕축구장',
            isAutoRental: true
          });
        }
      }
    }

    // 날짜순 정렬 후 상위 5개
    return allEvents
      .sort((a, b) => a.dateObj - b.dateObj)
      .slice(0, 5);
  };

  // 날짜 포맷 (YYYY-MM-DD)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 날짜 표시 포맷 (M월 D일 (요일))
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const weekday = weekdays[date.getDay()];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return `${month}월 ${day}일 (${weekday}) · 오늘`;
    } else if (date.getTime() === tomorrow.getTime()) {
      return `${month}월 ${day}일 (${weekday}) · 내일`;
    }

    return `${month}월 ${day}일 (${weekday})`;
  };

  // D-Day 계산
  const getDday = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(dateStr + 'T00:00:00');
    const diff = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));

    if (diff === 0) return '오늘';
    if (diff === 1) return '내일';
    if (diff > 0) return `D-${diff}`;
    return '';
  };

  // 해당 월의 첫날과 마지막 날 구하기
  const getMonthInfo = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const prevMonthLastDay = new Date(year, month, 0);

    return {
      year,
      month,
      firstDay,
      lastDay,
      prevMonthLastDay,
      firstDayOfWeek: firstDay.getDay(),
      daysInMonth: lastDay.getDate()
    };
  };

  // 이전 달로 이동
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // 다음 달로 이동
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // 오늘로 이동
  const goToToday = () => {
    setCurrentDate(new Date());
    setViewMode('calendar');
  };

  // 해당 날짜가 일요일인지 확인
  const isSunday = (year, month, day) => {
    return new Date(year, month, day).getDay() === 0;
  };

  // 해당 날짜의 일정 가져오기
  const getEventsForDate = (year, month, day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date === dateStr);
  };

  // 매주 일요일 자동 대관 표시
  const hasRegularRental = (year, month, day) => {
    if (!isSunday(year, month, day)) return false;

    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dateEvents = events.filter(event => event.date === dateStr);

    const hasNoRental = dateEvents.some(event => event.type === 'no-rental');
    if (hasNoRental) return false;

    const hasExplicitRental = dateEvents.some(event => event.type === 'rental');
    if (hasExplicitRental) return false;

    return true;
  };

  // 캘린더 날짜 그리드 생성
  const renderCalendarDays = () => {
    const monthInfo = getMonthInfo(currentDate);
    const days = [];
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    // 이전 달 날짜들
    for (let i = 0; i < monthInfo.firstDayOfWeek; i++) {
      const prevMonthDay = monthInfo.prevMonthLastDay.getDate() - monthInfo.firstDayOfWeek + i + 1;
      days.push(
        <div key={`prev-${i}`} className="calendar-day other-month">
          <span className="day-number">{prevMonthDay}</span>
        </div>
      );
    }

    // 현재 달 날짜들
    for (let day = 1; day <= monthInfo.daysInMonth; day++) {
      const dateStr = `${monthInfo.year}-${String(monthInfo.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;
      const dayEvents = getEventsForDate(monthInfo.year, monthInfo.month, day);
      const autoRental = hasRegularRental(monthInfo.year, monthInfo.month, day);

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSunday(monthInfo.year, monthInfo.month, day) ? 'sunday' : ''}`}
          onClick={() => handleDateClick(monthInfo.year, monthInfo.month, day)}
        >
          <span className="day-number">{day}</span>
          <div className="event-dots">
            {autoRental && (
              <span className="event-dot rental" title="의왕축구장"></span>
            )}
            {dayEvents.map(event => (
              <span key={event.id} className={`event-dot ${event.type}`} title={getEventTypeLabel(event.type)}></span>
            ))}
          </div>
        </div>
      );
    }

    // 다음 달 날짜들
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <div key={`next-${i}`} className="calendar-day other-month">
          <span className="day-number">{i}</span>
        </div>
      );
    }

    return days;
  };

  // 날짜 클릭 핸들러
  const handleDateClick = (year, month, day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate({ year, month, day, dateStr });
    setShowEventModal(true);
  };

  if (loading) {
    return <div className="calendar-page"><div className="loading">로딩 중...</div></div>;
  }

  const monthInfo = getMonthInfo(currentDate);
  const upcomingEvents = getUpcomingEvents();

  return (
    <div className="calendar-page">
      {/* 페이지 헤더 */}
      <div className="page-header">
        <h2>일정</h2>
      </div>

      {/* 다가오는 일정 */}
      <div className="upcoming-section">
        <h3>다가오는 일정</h3>
        {upcomingEvents.length > 0 ? (
          <div className="upcoming-list">
            {upcomingEvents.map((event, index) => (
              <div key={event.id || index} className={`upcoming-item ${event.type}`}>
                <div className="upcoming-date">
                  <div className="date-display">{formatDateDisplay(event.date)}</div>
                  <div className="dday">{getDday(event.date)}</div>
                </div>
                <div className="upcoming-content">
                  <div className="event-type-icon">{getEventIcon(event.type)}</div>
                  <div className="event-info">
                    <div className="event-title">{event.title || getEventTypeLabel(event.type)}</div>
                    {event.location && <div className="event-location">📍 {event.location}</div>}
                    {event.time && <div className="event-time">⏰ {event.time}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-data">예정된 일정이 없습니다.</div>
        )}
      </div>

      {/* 뷰 전환 탭 */}
      <div className="view-toggle">
        <button
          className={viewMode === 'list' ? 'active' : ''}
          onClick={() => setViewMode('list')}
          aria-label="리스트 보기"
        >
          <i className="fas fa-list"></i>
          <span>리스트</span>
        </button>
        <button
          className={viewMode === 'calendar' ? 'active' : ''}
          onClick={() => setViewMode('calendar')}
          aria-label="캘린더 보기"
        >
          <i className="fas fa-calendar"></i>
          <span>캘린더</span>
        </button>
      </div>

      {/* 리스트 뷰 */}
      {viewMode === 'list' && (
        <div className="list-view">
          <div className="month-selector">
            <button onClick={goToPrevMonth} aria-label="이전 달">
              <i className="fas fa-chevron-left"></i>
            </button>
            <h3>{monthInfo.year}년 {monthInfo.month + 1}월</h3>
            <button onClick={goToNextMonth} aria-label="다음 달">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>

          <div className="events-list">
            {(() => {
              const monthEvents = [];

              // 해당 월의 모든 명시적 이벤트
              events.forEach(event => {
                const eventDate = new Date(event.date + 'T00:00:00');
                if (eventDate.getFullYear() === monthInfo.year &&
                    eventDate.getMonth() === monthInfo.month) {
                  monthEvents.push({
                    ...event,
                    dateObj: eventDate,
                    isAutoRental: false
                  });
                }
              });

              // 해당 월의 자동 일요일 대관
              for (let day = 1; day <= monthInfo.daysInMonth; day++) {
                if (hasRegularRental(monthInfo.year, monthInfo.month, day)) {
                  const dateStr = `${monthInfo.year}-${String(monthInfo.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  monthEvents.push({
                    id: 'auto-' + dateStr,
                    type: 'rental',
                    title: '정기 대관',
                    date: dateStr,
                    dateObj: new Date(monthInfo.year, monthInfo.month, day),
                    location: '의왕축구장',
                    isAutoRental: true
                  });
                }
              }

              // 날짜순 정렬
              monthEvents.sort((a, b) => a.dateObj - b.dateObj);

              if (monthEvents.length === 0) {
                return <div className="no-data">이번 달 일정이 없습니다.</div>;
              }

              return monthEvents.map((event, index) => (
                <div key={event.id || index} className={`list-event-item ${event.type}`}>
                  <div className="list-event-date">
                    <div className="date-num">{event.dateObj.getDate()}</div>
                    <div className="date-day">
                      {['일', '월', '화', '수', '목', '금', '토'][event.dateObj.getDay()]}
                    </div>
                  </div>
                  <div className="list-event-content">
                    <div className="list-event-header">
                      <span className="event-type-icon">{getEventIcon(event.type)}</span>
                      <span className={`event-type-label ${event.type}`}>
                        {getEventTypeLabel(event.type)}
                      </span>
                    </div>
                    <div className="list-event-title">{event.title || getEventTypeLabel(event.type)}</div>
                    {event.location && <div className="list-event-meta">📍 {event.location}</div>}
                    {event.time && <div className="list-event-meta">⏰ {event.time}</div>}
                    {event.memo && <div className="list-event-memo">{event.memo}</div>}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* 캘린더 뷰 */}
      {viewMode === 'calendar' && (
        <div className="calendar-view">
          {/* 월 네비게이션 */}
          <div className="month-nav">
            <h3>{monthInfo.year}년 {monthInfo.month + 1}월</h3>
            <div className="month-nav-buttons">
              <button onClick={goToPrevMonth} aria-label="이전 달">
                <i className="fas fa-chevron-left"></i>
              </button>
              <button onClick={goToNextMonth} aria-label="다음 달">
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>

          {/* 범례 */}
          <div className="calendar-legend">
            <span className="legend-item">
              <span className="legend-icon">⚽</span>
              <span className="legend-color rental"></span>
              의왕축구장
            </span>
            <span className="legend-item">
              <span className="legend-icon">📌</span>
              <span className="legend-color event"></span>
              행사
            </span>
            <span className="legend-item">
              <span className="legend-icon">🏆</span>
              <span className="legend-color tournament"></span>
              대회
            </span>
            <span className="legend-item">
              <span className="legend-icon">🎉</span>
              <span className="legend-color holiday"></span>
              연휴
            </span>
          </div>

          {/* 캘린더 그리드 */}
          <div className="calendar-grid">
            <div className="calendar-weekdays">
              <div className="weekday sunday">일</div>
              <div className="weekday">월</div>
              <div className="weekday">화</div>
              <div className="weekday">수</div>
              <div className="weekday">목</div>
              <div className="weekday">금</div>
              <div className="weekday">토</div>
            </div>
            <div className="calendar-days">
              {renderCalendarDays()}
            </div>
          </div>
        </div>
      )}

      {/* 일정 상세 모달 */}
      {showEventModal && selectedDate && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedDate.month + 1}월 {selectedDate.day}일 일정</h3>
            <div className="date-events-list">
              {hasRegularRental(selectedDate.year, selectedDate.month, selectedDate.day) && (
                <div className="event-item rental">
                  <span className="event-type-icon">⚽</span>
                  <div className="event-details">
                    <div className="event-type-badge rental">의왕축구장</div>
                    <div className="event-title">정기 대관 (일요일)</div>
                  </div>
                </div>
              )}
              {getEventsForDate(selectedDate.year, selectedDate.month, selectedDate.day).map(event => (
                <div key={event.id} className={`event-item ${event.type}`}>
                  <span className="event-type-icon">{getEventIcon(event.type)}</span>
                  <div className="event-details">
                    <div className={`event-type-badge ${event.type}`}>
                      {getEventTypeLabel(event.type)}
                    </div>
                    <div className="event-title">{event.title}</div>
                    {event.time && <div className="event-meta">⏰ {event.time}</div>}
                    {event.location && <div className="event-meta">📍 {event.location}</div>}
                    {event.memo && <div className="event-memo">{event.memo}</div>}
                  </div>
                </div>
              ))}
              {!hasRegularRental(selectedDate.year, selectedDate.month, selectedDate.day) &&
               getEventsForDate(selectedDate.year, selectedDate.month, selectedDate.day).length === 0 && (
                <div className="no-events">등록된 일정이 없습니다.</div>
              )}
            </div>
            <button className="close-modal-btn" onClick={() => setShowEventModal(false)}>
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;
